import React from 'react';
import { View, Text, TextInput, StyleSheet, Platform } from 'react-native';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { ACCESSORY_VIEW_ID } from '../shared/KeyboardDismissToolbar';

export interface RangeInputProps {
  label: string;
  minValue: string;
  maxValue: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  unit: string;
  placeholder: { min: string; max: string };
  helperText?: string;
  disabled?: boolean;
  error?: string;
  /** Shown for accessibility; screen reader Min/Max on inputs */
  accessibilityLabelBase?: string;
}

const numericOk = (text: string): boolean =>
  text === '' || /^\d*\.?\d*$/.test(text);

export function RangeInput({
  label,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  unit,
  placeholder,
  helperText,
  disabled = false,
  error,
  accessibilityLabelBase = 'Approximate kW',
}: RangeInputProps) {
  const { canEditRequest } = useFormPermissions();
  const hasError = Boolean(error);
  const isDisabled = disabled || !canEditRequest;
  const minStr = minValue == null ? '' : String(minValue);
  const maxStr = maxValue == null ? '' : String(maxValue);
  const baseA11y = accessibilityLabelBase;

  return (
    <View style={styles.formGroup} testID="range-input-container">
      {label.trim() ? <Text style={styles.label}>{label}</Text> : null}
      <View style={[styles.row, hasError && styles.rowError, isDisabled && styles.rowDisabled]}>
        <TextInput
          key={Platform.OS === 'ios' ? `min-${isDisabled}` : undefined}
          style={[styles.input, isDisabled && styles.inputDisabled]}
          value={minStr}
          onChangeText={(t) => numericOk(t) && onMinChange(t)}
          placeholder={placeholder.min}
          placeholderTextColor="#999"
          editable={!isDisabled}
          keyboardType="numeric"
          returnKeyType="done"
          inputAccessoryViewID={Platform.OS === 'ios' && !isDisabled ? ACCESSORY_VIEW_ID : undefined}
          accessibilityLabel={`${baseA11y} ${placeholder.min}`}
        />
        <Text style={styles.dash}>to</Text>
        <TextInput
          key={Platform.OS === 'ios' ? `max-${isDisabled}` : undefined}
          style={[styles.input, isDisabled && styles.inputDisabled]}
          value={maxStr}
          onChangeText={(t) => numericOk(t) && onMaxChange(t)}
          placeholder={placeholder.max}
          placeholderTextColor="#999"
          editable={!isDisabled}
          keyboardType="numeric"
          returnKeyType="done"
          inputAccessoryViewID={Platform.OS === 'ios' && !isDisabled ? ACCESSORY_VIEW_ID : undefined}
          accessibilityLabel={`${baseA11y} ${placeholder.max}`}
        />
        <Text style={styles.unit}>{unit}</Text>
      </View>
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
      {helperText && !error ? <Text style={styles.helperText}>{helperText}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  formGroup: { marginBottom: 16 },
  label: { fontSize: 14, fontWeight: '500', color: '#333', marginBottom: 6 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#fff',
    gap: 8,
  },
  rowError: { borderColor: '#e74c3c' },
  rowDisabled: { backgroundColor: '#f5f5f5' },
  input: {
    flex: 1,
    minWidth: 64,
    paddingHorizontal: 8,
    paddingVertical: Platform.OS === 'web' ? 8 : 6,
    fontSize: 14,
    color: '#333',
  },
  inputDisabled: { color: '#999' },
  dash: { fontSize: 14, color: '#666' },
  unit: { fontSize: 13, color: '#666', fontWeight: '500' },
  errorText: { fontSize: 12, color: '#e74c3c', marginTop: 4 },
  helperText: { fontSize: 12, color: '#666', marginTop: 4 },
});
